// THE SHELL THE FOUR STATIC PAGES SHARE — /about /terms /privacy /contact.
//
// It exists so those pages cannot drift away from the home page: the SAME
// PublicNav and the SAME PublicFooter, with a contained column between them
// instead of the home page's full-bleed bands. Nothing here knows which of the
// four it is wrapping.
//
// A page hands in a title, a lead paragraph and an optional `note` — the
// visible "Draft — to be reviewed" / "Placeholder" banner that Terms, Privacy
// and Contact carry, so nobody mistakes unapproved boilerplate for the real
// thing.

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import PublicNav from '../../components/public/PublicNav';
import PublicFooter from '../../components/public/PublicFooter';

/** One heading + body block; every static page is a stack of these. */
export function Section({ heading, children, testId }) {
  return (
    <section className="mt-9" data-testid={testId}>
      <h2 className="text-lg font-bold text-gray-900 sm:text-xl">{heading}</h2>
      <div className="mt-2.5 space-y-3 text-[15px] leading-relaxed text-gray-600">{children}</div>
    </section>
  );
}

export default function PublicPage({ title, lead, note, children, testId }) {
  const { t } = useTranslation('publicPages');

  return (
    <div className="flex min-h-screen w-full flex-col bg-white" data-testid={testId}>
      <PublicNav />
      <main className="flex-1 bg-[#f8fefD] pt-16">
        <div className="mx-auto w-full max-w-4xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#2D8A8A] transition-colors hover:text-[#4ABDBD]"
            data-testid="public-page-back"
          >
            <ArrowLeft size={16} /> {t('publicPages.back_home')}
          </Link>

          <h1 className="gradient-heading mt-6" data-testid="public-page-title">{title}</h1>
          <p className="text-base leading-relaxed text-gray-600 sm:text-lg" data-testid="public-page-lead">
            {lead}
          </p>

          {note && (
            <p
              className="mt-8 flex items-start gap-3 rounded-2xl border-2 border-[#D7A05C]/40 bg-[#D7A05C]/10 px-5 py-4 text-sm font-semibold leading-relaxed text-[#8a5f22]"
              data-testid="public-page-note"
            >
              <AlertTriangle size={18} className="mt-0.5 shrink-0 text-[#B07C36]" />
              {note}
            </p>
          )}

          <div className="mt-2">{children}</div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
